import api from '@/services/api';
import { learningProgressService } from '@/services/learningProgress';
import filter from 'lodash/filter';

// Types for conversation API
// Progress Data Types
export interface SkillScore {
    average_score: number;
    completion_rate: number;
    total_activities: number;
    completed_activities: number;
}

export interface ProgressSummary {
    progress_percentage: number;
    completion_rate: number;
    total_activities: number;
    completed_activities: number;
    passed_activities: number;
    total_lessons: number;
    completed_lessons: number;
    overall_score: number;
    pass_rate: number;
}

export interface ActivitiesSummary {
    total: number;
    completed: number;
    passed: number;
    completion_rate: number;
    pass_rate: number;
    average_score: number;
}

export interface LessonsSummary {
    total: number;
    completed: number;
    completion_rate: number;
    average_score: number;
}

export interface SkillScores {
    vocabulary: SkillScore;
    grammar: SkillScore;
    reading: SkillScore;
    speaking: SkillScore;
    listening: SkillScore;
    writing: SkillScore;
}

export interface TimeStatistics {
    total_time: number;
    average_time_per_lesson: number;
    session_duration: number;
}

export interface OverallStatistics {
    activities_summary: ActivitiesSummary;
    lessons_summary: LessonsSummary;
    skill_scores: SkillScores;
    time_statistics: TimeStatistics;
}

export interface SessionCompletionData {
    next_lesson_id: string;
    id: string;
    daily_schedule_id: string;
    daily_schedule: {
        id: string;
        day_of_week: string;
        estimated_duration: string;
        is_completed: boolean;
        completion_percentage: number;
        started_at: string | null;
        completed_at: string | null;
    };
    session_type: string;
    current_lesson_index: number;
    total_lessons: number;
    is_completed: boolean;
    created_at: string;
    updated_at: string;
    completed_at: string;
    progress_summary: ProgressSummary;
    overall_statistics: OverallStatistics;
    lessons_completed: any[];
    lessons_scores: Record<string, any>;
}

export interface ConversationSession {
    id: string;
    user?: {
        id: string;
        username: string;
        first_name: string;
        last_name: string;
        email: string;
    };
    user_id?: string; // For backward compatibility
    daily_schedule_id?: string; // ID of the associated daily schedule
    daily_schedule?: {
        id: string;
        day_of_week: string;
        estimated_duration: string;
        is_completed: boolean;
        completion_percentage: number;
        target_skills: string[];
        difficulty_preference: string;
        learning_mode: string;
        schedule_date?: string;
        created_at: string;
        updated_at: string;
    };
    base_session: {
        id: number;
        user: string;
        status: string;
    };
    session_type: 'daily_lesson' | 'practice' | 'assessment' | 'conversation';
    current_activity_index?: number;
    current_lesson_index: number;
    total_lessons: number;
    is_completed: boolean;
    lessons_completed: number[];
    lessons_scores: Record<string, number>;
    conversation_settings: {
        difficulty_level: string;
        ai_personality: string;
        feedback_frequency: string;
        skill_focus?: string;
    };
    avatar_settings: {
        avatar_id: string;
        voice_type: string;
        appearance: string;
    };
    time_spent: number;
    session_data: Record<string, any>;
    progress_percentage: number;
    created_at: string;
    updated_at: string;
    completed_at?: string;
    lesson_contents?: LessonContentDetailed[];
    messages?: ConversationMessage[];
    progress?: ConversationProgress;
    feedback?: ConversationFeedback;
    analytics?: ConversationStats;
    // Enhanced lesson and activity data for schedule-based sessions
    schedule_lessons?: DailyScheduleLesson[];
    current_schedule_lesson?: DailyScheduleLesson;
    current_ai_lesson?: AILesson;
    current_activities?: AIActivity[];
    current_activity_id?: string | number;
    next_activity_id?: string | number;
    lesson_title?: string;
}

export interface CompleteSessionData {
    timeSpent: number;
}

export interface AIConversationSession {
    id: string;
    user_id: string | number;
    title?: string;
    language: string;
    created_at: string;
    updated_at: string;
    is_active: boolean;
    settings: {
        ai_personality: string;
        difficulty_level: string;
        voice_type: string;
        feedback_frequency: string;
    };
    messages: ConversationMessage[];
    statistics: {
        total_messages: number;
        user_messages: number;
        ai_messages: number;
        session_duration: number;
    };
}

export interface AIConversationMessage {
    id: string;
    session_id: string;
    message_type: 'user_text' | 'user_voice' | 'ai_text' | 'ai_voice' | 'system';
    content: string;
    audio_url?: string;
    image_url?: string;
    sequence_number: number;
    timestamp: string;
    metadata: {
        suggested_questions?: string[];
        ai_emotion?: string;
        confidence_score?: number;
        processing_time?: number;
    };
}

export interface AIConversationFilters {
    user_id?: string | number;
    is_active?: boolean;
    language?: string;
    date_from?: string;
    date_to?: string;
    params?: {
        title?: string;
    };
}

export interface AIConversationResponse {
    results: AIConversationSession[];
    count: number;
    next?: string;
    previous?: string;
}

export interface LessonContent {
    subtopics: Array<{
        id: string;
        name: string;
        module_type: 'reading' | 'speaking' | 'vocabulary' | 'syllabus' | 'general';
        content_data: any;
        questions: Question[];
        exercises?: Exercise[];
    }>;
    total_duration_minutes: number;
    difficulty_level: string;
    skills_covered: string[];
}

export interface LessonContentDetailed {
    id: string;
    lesson_order: number;
    lesson_title: string;
    lesson_description: string;
    is_started: boolean;
    is_completed: boolean;
    completion_score?: number;
    time_spent?: string;
    status: 'pending' | 'active' | 'completed' | 'skipped' | 'failed';
    lesson_data: Record<string, any>;
    started_at?: string;
    completed_at?: string;
    subtopic_mapping?: {
        id: string;
        daily_schedule: string;
        subtopic_submodule: any;
        is_completed: boolean;
        completed_at?: string;
    };
    content_type_name?: string;
    ai_lesson?: any;
    progress_percentage: number;
    created_at: string;
}

export interface Question {
    id: number;
    question_text: string;
    question_type: string;
    answer_options?: any[];
    correct_answer?: any;
    metadata?: any;
    module_source: 'reading' | 'speaking' | 'vocabulary' | 'syllabus';
}

export interface Exercise {
    id: string;
    title: string;
    exercise_type: string;
    module_source: 'reading' | 'speaking' | 'vocabulary' | 'syllabus';
    content: any;
    difficulty: string;
}

export interface ConversationMessage {
    id: string;
    message_type:
    | 'user_text'
    | 'user_voice'
    | 'ai_text'
    | 'ai_voice'
    | 'system_message'
    | 'lesson_content'
    | 'exercise';
    content: string;
    audio_url?: string;
    image_url?: string;
    sequence_number: number;
    is_pinned: boolean;
    lesson_content?: LessonContentDetailed;
    question_reference?: string;
    question_data?: {
        id: string;
        question_text: string;
        question_type: string;
        options?: any[];
        correct_answer?: any;
        explanation?: string;
        difficulty_level?: string;
    };
    message_data: Record<string, any>;
    user_response?: Record<string, any>;
    is_correct?: boolean;
    response_score?: number;
    timestamp: string;
    updated_at: string;
}

export interface ConversationProgress {
    id: string;
    speaking_session?: any;
    reading_session?: any;
    total_questions_answered: number;
    correct_answers: number;
    speaking_accuracy: number;
    reading_accuracy: number;
    vocabulary_mastery: number;
    total_conversation_time: string;
    speaking_time: string;
    reading_time: string;
    vocabulary_time: string;
    detailed_progress: Record<string, any>;
    overall_accuracy: number;
    created_at: string;
    updated_at: string;
    // Legacy fields for backward compatibility
    session_id?: string;
    current_subtopic_index?: number;
    current_question_index?: number;
    completed_questions?: number;
    total_questions?: number;
    skill_scores?: Record<string, number>;
    time_spent_minutes?: number;
    progress_percentage?: number;
}

export interface ConversationFeedback {
    id: string;
    overall_feedback: string;
    overall_score: number;
    speaking_feedback: string;
    reading_feedback: string;
    vocabulary_feedback: string;
    comprehension_feedback: string;
    strengths: string[];
    areas_for_improvement: string[];
    recommendations: string[];
    suggested_next_lessons: string[];
    practice_suggestions: string[];
    achievements_unlocked: string[];
    encouragement_message: string;
    feedback_generated_at: string;
    ai_model_version: string;
    // Legacy fields for backward compatibility
    session_id?: string;
    skill_breakdown?: Record<
        string,
        {
            score: number;
            feedback: string;
            improvements: string[];
        }
    >;
    next_steps?: string[];
}

export interface ConversationAnalytics {
    id: string;
    message_count: number;
    voice_messages_count: number;
    text_messages_count: number;
    pinned_messages_count: number;
    response_times: Record<string, any>;
    interaction_patterns: Record<string, any>;
    difficulty_progression: Record<string, any>;
    learning_curve: Record<string, any>;
    completion_rate: number;
    engagement_score: number;
    satisfaction_score: number;
    audio_quality_scores: Record<string, any>;
    connection_issues: Record<string, any>;
    ai_generated_insights: Record<string, any>;
    created_at: string;
    updated_at: string;
}

// AI Lesson and Activity interfaces for schedule-based learning
export interface AILesson {
    id: string;
    lesson_id: string;
    title: string;
    lesson_mode: 'new' | 'review' | 'assessment';
    xp: number;
    estimated_duration_minutes: number;
    difficulty: 'easy' | 'medium' | 'hard';
    focus: string[];
    learning_style: string[];
    learning_objectives: string[];
    grammar_points: string[];
    vocab_points: string[];
    subtopic: {
        id: string;
        title: string;
        description: string;
        difficulty: string;
        topic: {
            id: string;
            title: string;
            description: string;
            syllabus: {
                id: string;
                level: string;
                target_level: string;
            };
        };
    };
    activities: AIActivity[];
    created_at: string;
    updated_at: string;
}

export interface AIActivity {
    id: string;
    activity_id: string;
    activity_type: string;
    activity_category:
    | 'h5p'
    | 'speaking'
    | 'reading'
    | 'vocabulary'
    | 'grammar'
    | 'listening'
    | 'writing'
    | 'conversation'
    | 'assessment'
    | 'quiz'
    | 'mixed';
    objective: string;
    description?: string;
    difficulty: 'easy' | 'medium' | 'hard' | 'adaptive';
    target_skills: string[];
    xp: number;
    expected_duration: number;
    max_attempts: number;
    passing_score: number;
    is_required: boolean;
    primary_content_type:
    | 'h5p'
    | 'speaking_exercise'
    | 'speaking'
    | 'reading_passage'
    | 'reading'
    | 'vocabulary'
    | 'vocabulary_word'
    | 'vocabulary_group'
    | 'custom'
    | 'external';
    primary_content_id?: string;
    activity_settings: Record<string, any>;
    feedback_settings: Record<string, any>;
    tags: string[];
    metadata: Record<string, any>;
    created_at: string;
    updated_at: string;
}

export interface DailyScheduleLesson {
    id: string;
    daily_schedule: string;
    ai_lesson: AILesson;
    sequence_order: number;
    priority: 'low' | 'medium' | 'high' | 'critical';
    is_started: boolean;
    is_completed: boolean;
    completion_percentage: number;
    estimated_duration: string;
    actual_duration?: string;
    started_at?: string;
    completed_at?: string;
    score?: number;
    attempts_count: number;
    difficulty_rating?: number;
    is_required: boolean;
    is_adaptive: boolean;
    lesson_data: Record<string, any>;
    created_at: string;
    updated_at: string;
}

export interface DailySchedule {
    id: string;
    weekly_plan: string;
    day_of_week: number;
    is_completed: boolean;
    subtopic_mappings: SubtopicMapping[];
}

export interface SubtopicMapping {
    id: string;
    daily_schedule: string;
    subtopic_submodule: any;
    is_completed: boolean;
    completed_at?: string;
}

export interface CreateConversationRequest {
    daily_schedule_id: string;
    session_type?: string;
    conversation_settings?: Record<string, any>;
    avatar_settings?: Record<string, any>;
}

export interface CreateAIConversationRequest {
    user_id: string | number;
    content_id?: string;
    content_type?: string;
    content?: string;
    language?: string;
    app_id?: string;
    action?: string;
}

export interface SubmitMessageRequest {
    session_id: string;
    message_type: 'user_text' | 'user_voice';
    content: string;
    audio_url?: string;
    image_url?: string;
    lesson_content_id?: string;
    user_response?: Record<string, any>;
    content_id?: string;
    content_type?: string;
    app_id?: string;
    action?: string;
}

export interface UpdateLessonProgressRequest {
    lesson_content_id: string;
    status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
    completion_score?: number;
    lesson_data?: Record<string, any>;
}

export interface ConversationStats {
    total_sessions: number;
    completed_sessions: number;
    total_messages: number;
    average_session_duration: number;
    overall_accuracy: number;
    favorite_lesson_types: string[];
    recent_achievements: string[];
}

const baseUrl = '/conversation';

async function getConversationSession(sessionId: string): Promise<ConversationSession> {
    try {
        const response = await api.get(`${baseUrl}/sessions/${sessionId}/`);
        return response.data;
    } catch (error) {
        console.error('Failed to get conversation session:', error);
        throw error;
    }
}

class ConversationService {
    // Session Management
    async createConversationFromDailySchedule(
        scheduleId: string,
        sessionConfig: {
            session_type: string;
            conversation_settings: any;
            avatar_settings: any;
        },
    ): Promise<ConversationSession> {
        try {
            // Validate that scheduleId is a valid UUID
            if (!scheduleId || scheduleId === 'undefined' || scheduleId === 'null') {
                throw new Error('Invalid schedule ID provided');
            }

            // Check if scheduleId is a valid UUID format
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
            if (!uuidRegex.test(scheduleId)) {
                throw new Error('Schedule ID must be a valid UUID');
            }

            // Get daily schedule with subtopics from learning progress
            const subtopics = await learningProgressService.getDailyScheduleWithSubtopics(scheduleId);

            // Generate lesson content by fetching data from different modules
            const lessonContent = await this.generateLessonContentFromSubtopics(subtopics);

            // Create conversation session
            const response = await api.post(`${baseUrl}/sessions/`, {
                session_type: sessionConfig.session_type,
                daily_schedule_id: scheduleId,
                conversation_settings: sessionConfig.conversation_settings,
                avatar_settings: sessionConfig.avatar_settings,
                lesson_content: lessonContent,
            });

            const session = response.data;

            // Fetch AI lessons from the schedule
            try {
                const scheduleLessons = await this.getAILessonsFromSchedule(scheduleId);
                session.schedule_lessons = scheduleLessons;
                session.total_lessons = scheduleLessons.length;

                if (scheduleLessons.length > 0) {
                    // Get the first lesson to start with
                    const firstScheduleLesson = scheduleLessons[0];
                    session.current_schedule_lesson = firstScheduleLesson;

                    // Fetch detailed lesson data with activities
                    const lessonWithActivities = await this.getScheduleLessonWithActivities(
                        session.id,
                        firstScheduleLesson.id,
                    );
                    session.current_ai_lesson = lessonWithActivities.ai_lesson;
                    session.current_activities = lessonWithActivities.ai_lesson.activities;
                    session.current_activity_index = 0;
                    session.lesson_title = lessonWithActivities.ai_lesson.title;
                    session.current_lesson_index = 0;
                }
            } catch (lessonError) {
                console.warn('Failed to fetch lesson data for schedule:', lessonError);
                // Continue with basic session if lesson fetching fails
            }

            return session;
        } catch (error) {
            console.error('Failed to create conversation from daily schedule:', error);
            throw error;
        }
    }

    async createSession(sessionData: Partial<ConversationSession>): Promise<ConversationSession> {
        try {
            const response = await api.post(`${baseUrl}/sessions/`, sessionData);
            return response.data;
        } catch (error) {
            console.error('Failed to create conversation session:', error);
            throw error;
        }
    }

    async getSession(sessionId: string): Promise<ConversationSession> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch conversation session:', error);
            throw error;
        }
    }

    // Get session with detailed lesson and activity data
    async getSessionWithLessons(sessionId: string): Promise<ConversationSession> {
        try {
            const session = await new ConversationService().getSession(sessionId);

            return session;
        } catch (error) {
            console.error('Failed to fetch session with lessons:', error);
            throw error;
        }
    }

    async updateSession(sessionId: string, updates: Partial<ConversationSession>): Promise<ConversationSession> {
        try {
            const response = await api.patch(`${baseUrl}/sessions/${sessionId}/`, updates);
            return response.data;
        } catch (error) {
            console.error('Failed to update conversation session:', error);
            throw error;
        }
    }

    async endSession(sessionId: string): Promise<void> {
        try {
            await api.post(`${baseUrl}/sessions/${sessionId}/end/`);
        } catch (error) {
            console.error('Failed to end conversation session:', error);
            throw error;
        }
    }

    async getChatSessions(filters?: AIConversationFilters): Promise<AIConversationResponse> {
        const params = new URLSearchParams();

        if (filters?.user_id) params.append('user_id', filters.user_id.toString());
        if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
        if (filters?.language) params.append('language', filters.language);
        if (filters?.date_from) params.append('date_from', filters.date_from);
        if (filters?.date_to) params.append('date_to', filters.date_to);
        if (filters?.params?.title) params.append('title', filters.params.title);

        const response = await api.get(`${baseUrl}/sessions/chats/${params.toString() ? '?' + params.toString() : ''}`);
        return response.data;
    }

    async getChatDetailsSession(sessionId: string): Promise<AIConversationSession> {
        const response = await api.get(`${baseUrl}/sessions/${sessionId}/chat_details/`);
        return response.data;
    }

    // Message Management
    async sendMessage(
        request: SubmitMessageRequest,
    ): Promise<{ user_message: ConversationMessage; ai_response: ConversationMessage }> {
        try {
            const response = await api.post(`${baseUrl}/messages/submit/`, request);
            return {
                user_message: response.data.user_message,
                ai_response: response.data.ai_response,
            };
        } catch (error) {
            console.error('Failed to send message:', error);
            throw error;
        }
    }

    async sendAnonymousMessage(
        request: SubmitMessageRequest,
    ): Promise<{ user_message: ConversationMessage; ai_response: ConversationMessage }> {
        try {
            const response = await api.post(`${baseUrl}/messages/anonymous-submit/`, request);
            return {
                user_message: response.data.user_message,
                ai_response: response.data.ai_response,
            };
        } catch (error) {
            console.error('Failed to send anonymous message:', error);
            throw error;
        }
    }

    async getMessages(sessionId: string, limit = 50, offset = 0): Promise<ConversationMessage[]> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/messages/`, {
                params: { limit, offset },
            });
            return response.data.results || response.data;
        } catch (error) {
            console.error('Failed to fetch messages:', error);
            return [];
        }
    }

    // Content Generation from Different Modules
    private async generateLessonContentFromSubtopics(subtopics: any[]): Promise<LessonContent> {
        const lessonSubtopics = await Promise.all(
            subtopics.map(async (subtopic) => {
                const moduleType = this.determineModuleType(subtopic.subtopic_submodule);

                // Fetch content and questions based on module type
                const { contentData, questions, exercises } = await this.fetchModuleContent(
                    moduleType,
                    subtopic.subtopic_submodule,
                    subtopic.question_mappings,
                );

                return {
                    id: subtopic.id,
                    name: subtopic.subtopic_submodule.name,
                    module_type: moduleType,
                    content_data: contentData,
                    questions,
                    exercises,
                };
            }),
        );

        return {
            subtopics: lessonSubtopics,
            total_duration_minutes: subtopics.reduce((total, s) => total + s.estimated_minutes, 0),
            difficulty_level: 'intermediate',
            skills_covered: this.extractSkillsCovered(lessonSubtopics),
        };
    }

    private determineModuleType(submodule: any): 'reading' | 'speaking' | 'vocabulary' | 'syllabus' | 'general' {
        // Determine module type based on resource_type or submodule data
        const resourceType = submodule.resource_type?.toLowerCase();

        if (resourceType?.includes('reading')) return 'reading';
        if (resourceType?.includes('speaking') || resourceType?.includes('pronunciation')) return 'speaking';
        if (resourceType?.includes('vocabulary') || resourceType?.includes('vocab')) return 'vocabulary';
        if (submodule.practice_type === 1) return 'syllabus'; // Practice type
        if (submodule.practice_type === 2) return 'syllabus'; // Learning type

        return 'general';
    }

    private async fetchModuleContent(
        moduleType: string,
        submodule: any,
        questionMappings: any[],
    ): Promise<{ contentData: any; questions: Question[]; exercises: Exercise[] }> {
        try {
            switch (moduleType) {
                case 'reading':
                    return await this.fetchReadingContent(submodule, questionMappings);
                case 'speaking':
                    return await this.fetchSpeakingContent(submodule, questionMappings);
                case 'vocabulary':
                    return await this.fetchVocabularyContent(submodule, questionMappings);
                case 'syllabus':
                default:
                    return await this.fetchSyllabusContent(submodule, questionMappings);
            }
        } catch (error) {
            console.error(`Failed to fetch ${moduleType} content:`, error);
            return { contentData: null, questions: [], exercises: [] };
        }
    }

    private async fetchReadingContent(
        submodule: any,
        questionMappings: any[],
    ): Promise<{ contentData: any; questions: Question[]; exercises: Exercise[] }> {
        try {
            // Get reading passages linked to this submodule
            const response = await api.get(`${baseUrl}/reading/passages/`, {
                params: {
                    submodule_id: submodule.id,
                    limit: 1,
                },
            });

            const passages = response.data.results || response.data;
            const passage = passages[0];

            if (!passage) {
                return { contentData: null, questions: [], exercises: [] };
            }

            // Get passage details with questions
            const detailResponse = await api.get(`${baseUrl}/reading/passages/${passage.id}/`);
            const passageDetail = detailResponse.data;

            const questions: Question[] =
                passageDetail.questions?.map((q: any) => ({
                    id: q.id,
                    question_text: q.question_text,
                    question_type: q.question_type,
                    answer_options: q.answer_options,
                    correct_answer: q.correct_answer,
                    metadata: {
                        skill_tested: q.skill_tested,
                        difficulty_level: q.difficulty_level,
                        points: q.points,
                    },
                    module_source: 'reading',
                })) || [];

            return {
                contentData: {
                    passage: passageDetail,
                    reading_mode: 'comprehension',
                },
                questions,
                exercises: [],
            };
        } catch (error) {
            console.error('Failed to fetch reading content:', error);
            return { contentData: null, questions: [], exercises: [] };
        }
    }

    private async fetchSpeakingContent(
        submodule: any,
        questionMappings: any[],
    ): Promise<{ contentData: any; questions: Question[]; exercises: Exercise[] }> {
        try {
            // Get speaking exercises linked to this submodule
            const response = await api.get(`${baseUrl}/speaking/exercises/`, {
                params: {
                    submodule_id: submodule.id,
                    limit: 5,
                },
            });

            const exercises = response.data.results || response.data;

            const speakingExercises: Exercise[] = exercises.map((ex: any) => ({
                id: ex.id,
                title: ex.title,
                exercise_type: ex.exercise_type,
                module_source: 'speaking',
                content: {
                    target_text: ex.target_text,
                    phonetic_transcription: ex.phonetic_transcription,
                    difficulty: ex.difficulty,
                    focus_areas: ex.focus_areas,
                },
                difficulty: ex.difficulty,
            }));

            // Convert exercises to questions for conversation flow
            const questions: Question[] = exercises.map((ex: any, index: number) => ({
                id: ex.id,
                question_text: `Practice speaking: "${ex.target_text}"`,
                question_type: 'speaking_practice',
                metadata: {
                    exercise_type: ex.exercise_type,
                    target_text: ex.target_text,
                    phonetic_transcription: ex.phonetic_transcription,
                    difficulty: ex.difficulty,
                },
                module_source: 'speaking',
            }));

            return {
                contentData: {
                    speaking_mode: 'pronunciation_practice',
                    exercises: exercises,
                },
                questions,
                exercises: speakingExercises,
            };
        } catch (error) {
            console.error('Failed to fetch speaking content:', error);
            return { contentData: null, questions: [], exercises: [] };
        }
    }

    private async fetchVocabularyContent(
        submodule: any,
        questionMappings: any[],
    ): Promise<{ contentData: any; questions: Question[]; exercises: Exercise[] }> {
        try {
            // Get vocabulary sets or words linked to this submodule
            const response = await api.get(`${baseUrl}/vocabulary/sets/`, {
                params: {
                    submodule_id: submodule.id,
                    limit: 1,
                },
            });

            const vocabularySets = response.data.results || response.data;
            const vocabSet = vocabularySets[0];

            if (!vocabSet) {
                return { contentData: null, questions: [], exercises: [] };
            }

            // Get words in the set
            const wordsResponse = await api.get(`${baseUrl}/vocabulary/sets/${vocabSet.id}/words/`);
            const words = wordsResponse.data.results || wordsResponse.data;

            // Generate vocabulary questions
            const questions: Question[] = words.slice(0, 10).map((word: any, index: number) => ({
                id: word.id,
                question_text: `What does "${word.word}" mean?`,
                question_type: 'vocabulary_definition',
                answer_options: this.generateVocabOptions(word.definition, words),
                correct_answer: word.definition,
                metadata: {
                    word: word.word,
                    definition: word.definition,
                    example_sentence: word.example_sentence,
                    difficulty_level: word.difficulty_level,
                },
                module_source: 'vocabulary',
            }));

            return {
                contentData: {
                    vocabulary_set: vocabSet,
                    words: words,
                    learning_mode: 'definition_practice',
                },
                questions,
                exercises: [],
            };
        } catch (error) {
            console.error('Failed to fetch vocabulary content:', error);
            return { contentData: null, questions: [], exercises: [] };
        }
    }

    private async fetchSyllabusContent(
        submodule: any,
        questionMappings: any[],
    ): Promise<{ contentData: any; questions: Question[]; exercises: Exercise[] }> {
        try {
            // Get syllabus questions directly
            const questionIds = questionMappings.map((qm) => qm.submodule_question_id);

            if (questionIds.length === 0) {
                return { contentData: null, questions: [], exercises: [] };
            }

            // Fetch questions from syllabus module
            const questionsResponse = await api.get(`${baseUrl}/syllabus/questions/`, {
                params: {
                    ids: questionIds.join(','),
                    sub_module_id: submodule.id,
                },
            });

            const syllabusQuestions = questionsResponse.data.results || questionsResponse.data;

            const questions: Question[] = syllabusQuestions.map((q: any) => ({
                id: q.id,
                question_text: q.name_question || 'Interactive Question',
                question_type: q.type_question || 'interactive',
                metadata: {
                    block_id: q.block_id_question,
                    graded: q.graded,
                    submodule_id: q.sub_modules,
                },
                module_source: 'syllabus',
            }));

            // Get submodule details
            const submoduleResponse = await api.get(`${baseUrl}/syllabus/submodules/${submodule.id}/`);
            const submoduleDetail = submoduleResponse.data;

            return {
                contentData: {
                    submodule: submoduleDetail,
                    interactive_mode: true,
                },
                questions,
                exercises: [],
            };
        } catch (error) {
            console.error('Failed to fetch syllabus content:', error);
            return { contentData: null, questions: [], exercises: [] };
        }
    }

    private generateVocabOptions(correctDefinition: string, allWords: any[]): string[] {
        // Generate 3 wrong options + 1 correct
        const wrongOptions = allWords
            .filter((w) => w.definition !== correctDefinition)
            .slice(0, 3)
            .map((w) => w.definition);

        const options = [correctDefinition, ...wrongOptions];
        return this.shuffleArray(options);
    }

    private shuffleArray(array: any[]): any[] {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    private extractSkillsCovered(subtopics: any[]): string[] {
        const skills = new Set<string>();

        subtopics.forEach((subtopic) => {
            switch (subtopic.module_type) {
                case 'reading':
                    skills.add('reading_comprehension');
                    skills.add('vocabulary');
                    break;
                case 'speaking':
                    skills.add('pronunciation');
                    skills.add('fluency');
                    skills.add('speaking');
                    break;
                case 'vocabulary':
                    skills.add('vocabulary');
                    skills.add('word_recognition');
                    break;
                case 'syllabus':
                    skills.add('general_practice');
                    break;
            }
        });

        return Array.from(skills);
    }

    // Progress and Feedback
    async updateProgress(
        sessionId: string,
        progressData: Partial<ConversationProgress>,
    ): Promise<ConversationProgress> {
        try {
            const response = await api.patch(`${baseUrl}/sessions/${sessionId}/progress/`, progressData);
            return response.data;
        } catch (error) {
            console.error('Failed to update conversation progress:', error);
            throw error;
        }
    }

    async getProgress(sessionId: string): Promise<ConversationProgress> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/progress/`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch conversation progress:', error);
            throw error;
        }
    }

    async generateFeedback(sessionId: string): Promise<ConversationFeedback> {
        try {
            const response = await api.post(`${baseUrl}/sessions/${sessionId}/feedback/generate/`);
            // Handle the new response format from backend
            return response.data.feedback || response.data;
        } catch (error) {
            console.error('Failed to generate conversation feedback:', error);
            throw error;
        }
    }

    async submitAnswer(sessionId: string, questionId: number, answer: any, timeSpent: number): Promise<boolean> {
        try {
            const response = await api.post(`${baseUrl}/sessions/${sessionId}/answers/`, {
                question_id: questionId,
                user_answer: answer,
                time_spent_seconds: timeSpent,
            });
            return response.data.is_correct || false;
        } catch (error) {
            console.error('Failed to submit answer:', error);
            throw error;
        }
    }

    // Analytics
    // Daily Schedule Integration
    async getAvailableDailySchedules(): Promise<any[]> {
        return learningProgressService.getAvailableSchedules();
    }

    // Session Management - Add missing methods
    async createConversationSession(request: CreateConversationRequest): Promise<ConversationSession> {
        try {
            const response = await api.post(`${baseUrl}/sessions/`, request);
            return response.data;
        } catch (error) {
            console.error('Failed to create conversation session:', error);
            throw error;
        }
    }

    async updateConversationSession(
        sessionId: string,
        data: Partial<ConversationSession>,
    ): Promise<ConversationSession> {
        try {
            const response = await api.put(`${baseUrl}/sessions/${sessionId}/`, data);
            return response.data;
        } catch (error) {
            console.error('Failed to update conversation session:', error);
            throw error;
        }
    }

    async completeConversationSession(sessionId: string, sessionData: ConversationSession): Promise<SessionCompletionData> {
        try {
            const response = await api.post(`${baseUrl}/sessions/${sessionId}/complete/`, sessionData);
            return response.data;
        } catch (error) {
            console.error('Failed to complete conversation session:', error);
            throw error;
        }
    }

    async getSessionLessons(sessionId: string): Promise<DailyScheduleLesson[]> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/lessons/`);
            return response.data;
        } catch (error) {
            console.error('Failed to get session lessons:', error);
            throw error;
        }
    }

    // Get AI Lessons from schedule_id via DailyScheduleLesson
    async getAILessonsFromSchedule(scheduleId: string): Promise<DailyScheduleLesson[]> {
        try {
            const response = await api.get(`/progress/daily-schedules/${scheduleId}/ai-lessons/`);
            return response.data || [];
        } catch (error) {
            console.error('Failed to get AI lessons from schedule:', error);
            throw error;
        }
    }

    // Get AILesson with activities from DailyScheduleLesson
    async getScheduleLessonWithActivities(
        sessionId: string,
        scheduleLessonId: string,
    ): Promise<{
        schedule_lesson_id: string;
        ai_lesson: AILesson;
        is_started: boolean;
        is_completed: boolean;
        completion_percentage: number;
        score?: number;
    }> {
        try {
            const response = await api.get(
                `${baseUrl}/sessions/${sessionId}/schedule-lesson/${scheduleLessonId}/activities/`,
            );
            return response.data;
        } catch (error) {
            console.error('Failed to get schedule lesson with activities:', error);
            throw error;
        }
    }

    // Get all activities for a lesson
    async getActivitiesForLesson(lessonId: string): Promise<AIActivity[]> {
        try {
            const response = await api.get(`/syllabus/ai-activities/?lesson_id=${lessonId}`);
            return response.data.results || response.data || [];
        } catch (error) {
            console.error('Failed to get activities for lesson:', error);
            throw error;
        }
    }

    // Start lesson from schedule
    async startScheduleLesson(scheduleLessonId: string): Promise<DailyScheduleLesson> {
        try {
            const response = await api.post(`/progress/schedule-lessons/${scheduleLessonId}/start/`);
            return response.data;
        } catch (error) {
            console.error('Failed to start schedule lesson:', error);
            throw error;
        }
    }

    // Complete lesson from schedule
    async completeScheduleLesson(scheduleLessonId: string, score?: number): Promise<DailyScheduleLesson> {
        try {
            const requestData: any = {};
            if (score !== undefined) {
                requestData.score = score;
            }
            const response = await api.post(`/progress/schedule-lessons/${scheduleLessonId}/complete`, requestData);
            return response.data;
        } catch (error) {
            console.error('Failed to complete schedule lesson:', error);
            throw error;
        }
    }

    // Submit activity answer
    async submitActivityAnswer(
        activityId: string,
        answer: any,
        timeSpent: number,
        sessionId?: string,
        scheduleLessonId?: string,
    ): Promise<{
        is_correct: boolean;
        is_passed: boolean;
        score: number;
        feedback?: string;
        activity_category: string;
        xp_earned: number;
        attempts_count: number;
        max_attempts: number;
        passing_score: number;
        next_activity_id?: string;
        auto_advance: boolean;
        lesson_completed?: boolean;
    }> {
        try {
            const response = await api.post(`/progress/daily-schedules/submit-activity-answer`, {
                activity_id: activityId,
                user_answer: answer,
                time_spent_seconds: timeSpent,
                conversation_session_id: sessionId,
                schedule_lesson_id: scheduleLessonId,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to submit activity answer:', error);
            throw error;
        }
    }

    // Update lesson progress
    async updateLessonProgress(
        lessonId?: string,
        scheduleLessonId?: string,
        activitiesCompleted?: string[],
        overallScore?: number,
        timeSpentSeconds?: number,
    ): Promise<{ message: string; completion_percentage: number; is_completed: boolean }> {
        try {
            const response = await api.post(`/progress/daily-schedules/update-lesson-progress`, {
                lesson_id: lessonId,
                schedule_lesson_id: scheduleLessonId,
                activities_completed: activitiesCompleted || [],
                overall_score: overallScore || 0,
                time_spent_seconds: timeSpentSeconds || 0,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to update lesson progress:', error);
            throw error;
        }
    }

    async startLesson(lessonId: string): Promise<LessonContent> {
        try {
            const response = await api.post(`${baseUrl}/lessons/${lessonId}/start/`);
            return response.data;
        } catch (error) {
            console.error('Failed to start lesson:', error);
            throw error;
        }
    }

    async completeLesson(
        lessonId: string,
        completionData: { completion_score?: number; time_spent?: string; lesson_data?: Record<string, any> },
    ): Promise<LessonContent> {
        try {
            const response = await api.post(`${baseUrl}/lessons/progress/`, {
                lesson_content_id: lessonId,
                status: 'completed',
                ...completionData,
            });
            return response.data;
        } catch (error) {
            console.error('Failed to complete lesson:', error);
            throw error;
        }
    }

    async getConversationSessions(): Promise<ConversationSession[]> {
        try {
            const response = await api.get(`${baseUrl}/sessions/`);
            // Handle new API response format where session is nested under "session" property
            if (response.data.session) {
                return [response.data.session]; // Return single session as array
            }
            // Handle case where no session is found
            return [];
        } catch (error: any) {
            // If 404, return empty array instead of throwing error
            if (error.response?.status === 404) {
                return [];
            }
            console.error('Failed to get conversation sessions:', error);
            throw error;
        }
    }

    async getConversationSession(sessionId: string): Promise<ConversationSession> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/`);
            return response.data.session;
        } catch (_error) {
            console.error('Failed to get  conversation session:', _error);
            throw _error;
        }
    }

    async getOrCreateConversationSession(
        scheduleId?: string,
        skill?: string,
        lessonIds?: number[],
        nextActivityId?: string,
        mode?: string,
        assessmentType?: string,
    ): Promise<ConversationSession> {
        try {
            const filteredLessonIds = filter(lessonIds);

            const response = await api.post(`${baseUrl}/sessions/get_or_create/`, {
                daily_schedule_id: scheduleId,
                skill,
                lesson_ids: filteredLessonIds.length > 0 ? filteredLessonIds : null,
                next_activity_id: nextActivityId,
                mode,
                assessment_type: assessmentType,
            });
            const session = response.data.session;

            // If this is a newly created session or has a daily_schedule, load enhanced data
            if (session.daily_schedule?.id || session.daily_schedule_id) {
                try {
                    const sessionWithLessons = await this.getSessionWithLessons(session.id);
                    return {
                        ...session,
                        ...sessionWithLessons,
                    };
                } catch (lessonError) {
                    console.warn('Failed to load lesson data for new session:', lessonError);
                    return session;
                }
            }

            return session;
        } catch (error: any) {
            console.error('Failed to get or create conversation session:', error);
            throw error;
        }
    }

    async getSessionMessages(sessionId: string): Promise<ConversationMessage[]> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/messages/`);
            return response.data;
        } catch (error) {
            console.error('Failed to get session messages:', error);
            throw error;
        }
    }

    async submitMessage(
        request: SubmitMessageRequest,
    ): Promise<{ user_message: ConversationMessage; ai_response: ConversationMessage }> {
        try {
            const response = await api.post(`${baseUrl}/messages/submit/`, request);
            return response.data;
        } catch (error) {
            console.error('Failed to submit message:', error);
            throw error;
        }
    }

    // Upload voice message to backend (convert to audio file and upload to S3)
    async uploadVoiceMessage(sessionId: string, audioBlob: Blob): Promise<{ audio_url: string }> {
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'voice_message.wav');
            formData.append('session_id', sessionId);

            const response = await api.post(`${baseUrl}/sessions/${sessionId}/upload-audio/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('Failed to upload voice message:', error);
            throw error;
        }
    }

    // Submit voice message with audio data (base64 or uploaded URL)
    async submitVoiceMessage(
        sessionId: string,
        audioData: string,
        audioBlob?: Blob,
    ): Promise<{ user_message: ConversationMessage; ai_response: ConversationMessage }> {
        try {
            let audioUrl = audioData;

            // If audioBlob is provided, upload it first
            if (audioBlob) {
                const uploadResult = await this.uploadVoiceMessage(sessionId, audioBlob);
                audioUrl = uploadResult.audio_url;
            }

            const request: SubmitMessageRequest = {
                session_id: sessionId,
                message_type: 'user_voice',
                content: 'Voice message',
                audio_url: audioUrl,
            };

            const response = await api.post(`${baseUrl}/messages/submit/`, request);
            return response.data;
        } catch (error) {
            console.error('Failed to submit voice message:', error);
            throw error;
        }
    }

    async pinMessage(messageId: string, isPinned: boolean = true): Promise<ConversationMessage> {
        try {
            const response = await api.post(`${baseUrl}/messages/${messageId}/pin/`, { is_pinned: isPinned });
            return response.data;
        } catch (error) {
            console.error('Failed to pin message:', error);
            throw error;
        }
    }

    async getPinnedMessages(sessionId: string): Promise<ConversationMessage[]> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/messages/?pinned=true`);
            return response.data;
        } catch (error) {
            console.error('Failed to get pinned messages:', error);
            throw error;
        }
    }

    async getSessionProgress(sessionId: string): Promise<ConversationProgress> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/progress/`);
            return response.data;
        } catch (error) {
            console.error('Failed to get session progress:', error);
            throw error;
        }
    }

    async updateSessionProgress(
        sessionId: string,
        progressData: Partial<ConversationProgress>,
    ): Promise<ConversationProgress> {
        try {
            const response = await api.patch(`${baseUrl}/sessions/${sessionId}/progress/`, progressData);
            return response.data;
        } catch (error) {
            console.error('Failed to update session progress:', error);
            throw error;
        }
    }

    async getSessionFeedback(sessionId: string): Promise<ConversationFeedback | null> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/feedback/`);
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.error('Failed to get session feedback:', error);
            throw error;
        }
    }

    async generateSessionFeedback(sessionId: string): Promise<ConversationFeedback> {
        try {
            // First check if session is completed
            console.log('Generating feedback for session:', sessionId);

            if (!sessionId) {
                throw new Error('Session ID is required to generate feedback');
            }

            const session = await getConversationSession(sessionId);
            if (!session || !session.is_completed) {
                throw new Error('Cannot generate feedback for incomplete session');
            }

            const response = await api.post(`${baseUrl}/sessions/${sessionId}/feedback/generate/`);
            // Handle the new response format from backend
            return response.data.feedback || response.data;
        } catch (error) {
            console.error('Failed to generate session feedback:', error);
            throw error;
        }
    }

    async getSessionAnalytics(sessionId: string): Promise<ConversationAnalytics> {
        try {
            const response = await api.get(`${baseUrl}/sessions/${sessionId}/analytics/`);
            return response.data;
        } catch (error) {
            console.error('Failed to get session analytics:', error);
            throw error;
        }
    }

    async createAIConversationSession(
        requestData: CreateAIConversationRequest,
    ): Promise<{ id: string; messages: ConversationMessage[] }> {
        try {
            const response = await api.post(`${baseUrl}/sessions/create_chat/`, requestData);
            return response.data;
        } catch (error) {
            console.error('Failed to create AI conversation session:', error);
            throw error;
        }
    }

    async createAnonymousAIConversationSession(
        requestData: CreateAIConversationRequest,
    ): Promise<{ id: string; messages: ConversationMessage[] }> {
        try {
            const response = await api.post(`${baseUrl}/sessions/anonymous-chat/`, requestData);
            return response.data;
        } catch (error) {
            console.error('Failed to create anonymous AI conversation session:', error);
            throw error;
        }
    }
}

export const conversationService = new ConversationService();
